import React, { useEffect, useState, useRef } from 'react';
import { Template } from '../core/template.schemas';
import { 
  useLoadTemplateFromAPI,
  useSaveTemplateToAPI,
  useTemplate,
  useTemplateStore
} from './hooks/useTemplateStore';
import { Toolbar } from './components/toolbar';
import { LayersPanel } from './components/layers-panel';
import { SlotPanel } from './components/slot-panel';
import { PropertiesPanel } from './components/properties-panel';
import { Canvas } from './components/canvas';
import { Button } from '@/ui-components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui-components/ui/tabs';
import { Layers, Settings, SquareDashed } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useInfiniteBackground } from './hooks/useInfiniteBackground';
import { useStagePanAndZoom } from './hooks/useStagePanAndZoom';
import { toast } from "@/lib/toast";
import Konva from 'konva';
import { useNavigate } from 'react-router-dom';
// import { useGoogleFonts } from './hooks/useGoogleFonts'; // Removed as unused

import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useTemplateAssets } from './hooks/useTemplateAssets';
import { LoadingScreen } from './components/loading-screen';
import { SentrySectionBoundary } from '@/util-components/error/sentry-section-boundary';

/**
 * Props for the TemplateEditor component
 */
interface TemplateEditorProps {
  /** Optional template ID to load from API */
  templateId?: string;
  /** Callback fired when template is saved */
  onSave?: (template: Template) => void;
  /** Whether to show close button in UI */
  showCloseButton?: boolean;
  /** Callback fired when editor is closed */
  onClose?: () => void;
}

/**
 * Template Editor Component
 * 
 * A comprehensive template editing interface with canvas, layers, slots, and properties panels.
 * Supports auto-save functionality, responsive design, and drag-and-drop operations.
 * 
 * @param templateId - Optional template ID to load from API
 * @param showCloseButton - Whether to display close button
 * @param onClose - Callback when editor is closed
 */
export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  templateId,
  showCloseButton = false,
  onClose
}) => {
  const template = useTemplate();
  const navigate = useNavigate();

  // API hooks for template operations
  const { loadTemplate: loadTemplateFromAPI, isLoading } = useLoadTemplateFromAPI();
  const { saveTemplate: saveTemplateToAPI } = useSaveTemplateToAPI();

  // UI state management
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [leftPanelTab, setLeftPanelTab] = useState<'layers' | 'slots'>('layers');
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Load fonts for the current template
  // Load fonts for the current template
  // const { ensureTemplateFontsLoaded } = useGoogleFonts(); // Replaced by useTemplateAssets
  const { loaded: assetsLoaded, progress: loadProgress } = useTemplateAssets(template);
  
  // Refs for canvas and auto-save functionality
  const stageRef = React.useRef<Konva.Stage>(null);
  const templateRef = useRef(template);
  const templateIdRef = useRef(templateId);

  const canvasWidth = template.canvas.width;
  const canvasHeight = template.canvas.height;

  const { camera, handleWheel, handleMouseDown } = useStagePanAndZoom(
    stageRef,
    canvasWidth,
    canvasHeight,
  );
  const backgroundStyle = useInfiniteBackground(camera);

  // Sync refs with current template and templateId
  useEffect(() => {
    templateRef.current = template;
    templateIdRef.current = templateId;
  }, [template, templateId]);

  // Handle responsive layout changes
  useEffect(() => {
    const checkScreenSize = () => {
      const newIsMobile = window.innerWidth < 1024;
      setIsMobile(newIsMobile);

      if (newIsMobile) {
        // Close panels on small screens for better UX
        if (window.innerWidth < 768) {
          setLeftPanelOpen(false);
          setRightPanelOpen(false);
        }
      } else {
        // Restore panels on desktop
        setLeftPanelOpen(true);
        setRightPanelOpen(true);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Beim Editor-Mount ohne templateId: Fonts für aktuelles Template laden -> Handled by useTemplateAssets
  // useEffect(() => {
  //   if (!templateId && template) {
  //     ensureTemplateFontsLoaded(template).catch((err) =>
  //       console.warn('Font preload (mount) failed:', err)
  //     );
  //   }
  // }, [templateId, template, ensureTemplateFontsLoaded]);

  // Load template data from API when templateId changes
  useEffect(() => {
    const loadTemplateData = async () => {
      if (templateId) {
        try {
          await loadTemplateFromAPI(templateId);
          // Fonts and assets are now loaded via useTemplateAssets hook which reacts to template changes
          // const tpl = useTemplateStore.getState().template;
          // await ensureTemplateFontsLoaded(tpl);
        } catch (error) {
          console.error('Template loading failed:', error);
          toast.error('Template konnte nicht geladen werden', {
            description: 'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
            duration: 5000,
            error,
          });
        }
      }
    };

    loadTemplateData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]); // ensureTemplateFontsLoaded removed from deps since it's handled by hook

  // Auto-save functionality: triggers on history changes (user actions only)
  useEffect(() => {
    let autoSaveTimer: NodeJS.Timeout;
    
    const unsubscribe = useTemplateStore.subscribe(
      (state) => state.historyIndex,
      (historyIndex) => {        
        // Reset timer on each history change
        clearTimeout(autoSaveTimer);
        
        const currentTemplate = useTemplateStore.getState().template;
        
        // Only auto-save valid templates with proper ID
        if (templateIdRef.current && currentTemplate.title !== 'New Template') {
          autoSaveTimer = setTimeout(async () => {
            try {
              await saveTemplateToAPI({ incrementVersion: false });
              toast.success('Template automatisch gespeichert', { duration: 2000 });
            } catch (error) {
              console.error('Auto-save operation failed:', error);
              toast.error('Automatisches Speichern fehlgeschlagen', { duration: 3000, error });
            }
          }, 10000);
        } else {
          console.log('Auto-save skipped: invalid template or missing ID');
        }
      }
    );

    return () => {
      clearTimeout(autoSaveTimer);
      unsubscribe();
    };
  }, [saveTemplateToAPI]);
  
  /**
   * Handles manual template save operation
   */
  const handleSave = async () => {
    try {
      await saveTemplateToAPI({ incrementVersion: true });
      toast.success('Template erfolgreich gespeichert!', {
        description: 'Alle Änderungen wurden erfolgreich gespeichert.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Manual save operation failed:', error);
      toast.error('Fehler beim Speichern', {
        description: 'Das Template konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
        duration: 5000,
        error,
      });
    }
  };

  // Enable keyboard shortcuts
  useKeyboardShortcuts(handleSave);

  /**
   * Handles drag operation start
   */
  const handleDragStart = () => {
    setIsDragging(true);
  };

  /**
   * Handles drag operation end
   */
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  /**
   * Opens slot panel in left sidebar
   */
  const handleOpenSlotPanel = () => {
    setLeftPanelTab('slots');
    setLeftPanelOpen(true);
  };

  /**
   * Handles navigation to newly created template
   * @param newTemplateId - ID of the newly created template
   */
  const handleCreateTemplate = (newTemplateId: string) => {
    navigate(`/post-generator/template-editor?templateId=${newTemplateId}`);
  };

  // Render loading state during template fetch
  if (isLoading && templateId) {
    return <LoadingScreen progress={0} />;
  }

  // Show loading screen while assets (fonts/images) are preloading
  if (!assetsLoaded && template) {
    return <LoadingScreen progress={loadProgress} />;
  }

  return (
    <div className="h-screen w-full flex flex-col bg-gray-50">
      {/* Mobile top bar */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLeftPanelOpen(!leftPanelOpen)}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <Layers className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRightPanelOpen(!rightPanelOpen)}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
        <h1 className="text-sm font-medium text-gray-900 truncate">{template.title}</h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          Speichern
        </Button>
      </div>

      <div className="flex-1 flex min-h-0 relative">
        {/* Left sidebar: layers and slots */}
        <div className={cn(
          "bg-white border-r border-gray-200 transition-all duration-300 ease-in-out flex-shrink-0 shadow-sm",
          leftPanelOpen ? "w-64 lg:w-72" : "w-0",
          // Desktop positioning with drag state handling
          "lg:relative absolute z-20 h-full",
          !isDragging && "lg:transform-none",
          // Mobile transform animations (disabled during drag)
          !isDragging && leftPanelOpen && isMobile ? "translate-x-0" : 
          !isDragging && isMobile ? "-translate-x-full" : "",
          // No transforms during drag operations
          isDragging && "transform-none"
        )}>
          {/* Tab navigation container */}
          <div className="h-full overflow-hidden">
            <div className="h-full">
              <Tabs value={leftPanelTab} onValueChange={(value) => setLeftPanelTab(value as 'layers' | 'slots')} className="h-full flex flex-col">
                {/* Tab navigation */}
                <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 bg-white">
                  <div className="bg-gray-100/60 rounded-lg p-0.5">
                    <TabsList className="grid w-full grid-cols-2 h-8 bg-transparent p-0 gap-0.5">
                      <TabsTrigger 
                        value="layers" 
                        className="
                          h-7 px-3 text-xs font-medium rounded-md
                          data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm
                          data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-800 data-[state=inactive]:hover:bg-white/50
                          transition-all duration-150 ease-out
                          border-0 ring-0 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:ring-offset-0
                        "
                      >
                        <Layers className="w-3 h-3 mr-1.5" />
                        Ebenen
                      </TabsTrigger>
                      <TabsTrigger 
                        value="slots" 
                        className="
                          h-7 px-3 text-xs font-medium rounded-md
                          data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm
                          data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-800 data-[state=inactive]:hover:bg-white/50
                          transition-all duration-150 ease-out
                          border-0 ring-0 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:ring-offset-0
                        "
                      >
                        <SquareDashed className="w-3 h-3 mr-1.5" />
                        Platzhalter
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </div>
                
                {/* Panel content */}
                <div className="flex-1 overflow-hidden">
                  <TabsContent value="layers" className="h-full m-0 p-0">
                    <LayersPanel 
                      className="border-0"
                      showCloseButton={false}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    />
                  </TabsContent>
                  <TabsContent value="slots" className="h-full m-0 p-0">
                    <SlotPanel 
                      className="border-0"
                      showCloseButton={false}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
        
        {/* Main canvas area */}
        <div 
          className="flex-1 min-w-0 flex flex-col bg-gray-50" 
          style={backgroundStyle}
        >
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <div 
              className="flex-shrink-0"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SentrySectionBoundary
                boundaryKey="template-editor-canvas"
                title="Canvas konnte nicht geladen werden"
                description="Der Editor-Bereich ist abgestürzt. Du kannst diesen Bereich neu laden, ohne die gesamte App neu zu starten."
                className="flex h-full w-full items-center justify-center p-8"
              >
                <Canvas 
                  templateWidth={canvasWidth} 
                  templateHeight={canvasHeight}
                  camera={camera}
                  stageRef={stageRef}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                />
              </SentrySectionBoundary>
            </div>
          </div>
        </div>
        
        {/* Right sidebar: properties */}
        <div className={cn(
          "bg-white border-l border-gray-200 transition-all duration-300 ease-in-out flex-shrink-0 shadow-sm",
          rightPanelOpen ? "w-64 lg:w-72" : "w-0",
          "lg:relative absolute right-0 lg:translate-x-0 z-20 h-full",
          rightPanelOpen && isMobile ? "translate-x-0" : isMobile ? "translate-x-full" : ""
        )}>
          <div className="h-full overflow-hidden">
            <PropertiesPanel 
              className="h-full"
              onClose={() => setRightPanelOpen(false)}
              showCloseButton={isMobile}
              onOpenSlotPanel={handleOpenSlotPanel}
            />
          </div>
        </div>

        {/* Mobile overlay */}
        {isMobile && (leftPanelOpen || rightPanelOpen) && (
          <div 
            className="absolute inset-0 bg-black/20 z-10"
            onClick={() => {
              setLeftPanelOpen(false);
              setRightPanelOpen(false);
            }}
          />
        )}
      </div>
      
      {/* Bottom toolbar */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 shadow-sm">
        <Toolbar onSave={handleSave} onCreateTemplate={handleCreateTemplate} stageRef={stageRef} />
      </div>
    </div>
  );
};

export default TemplateEditor;
